import React, { useEffect, useMemo, useRef } from "react";
import { useCallback, useState } from "react";
import { Dimensions, Modal, Platform, View } from "react-native";

import { useDebounce } from "@whoosh/utilities/src/hooks";

import { usePreventScroll } from "../../../hooks/usePreventScroll";
import { AnimationDurations } from "../../../theme/animations";
import { Box } from "../../primitive";
import { Overlay } from "../Overlay/Overlay";
import { ActionSheetContentContainer } from "./components/ActionSheetContentContainer";
import { MenuContent } from "./components/MenuContent";
import { ModalContentContainer } from "./components/ModalContentContainer";
import { useGetTriggerWidthAndTargetCoordinates } from "./hooks/useGetTriggerWidthAndTargetCoordinates";
import { useMenuKeyboardNavigation } from "./hooks/useMenuKeyboardNavigation";
import {
  BubbleCoordinates,
  CloseMenu,
  MenuComponent,
  MenuContext,
  MenuContextType,
  MenuProps,
} from "./types";

const MenuBase: MenuComponent = ({
  trigger,
  content,
  openPosition = "bottom-start",
  defaultIsOpen,
  overrideIsOpen,
  onClose,
  verticalOffset,
  horizontalOffset,
  showActionSheetDoneButton,
  neverOpenAsActionSheet,
}: MenuProps) => {
  const {
    triggerWidth,
    targetCoords,
    measureScreenPosition,
    triggerRef,
    modalBodyRef,
  } = useGetTriggerWidthAndTargetCoordinates(
    openPosition,
    verticalOffset,
    horizontalOffset
  );
  /**
   * We store oure 'open' state as well as our 'is closing'
   * state. We need to store isClosing so that we can manage
   * our exit animation
   */
  const [isOpen, setIsOpen] = useState(
    defaultIsOpen || overrideIsOpen || false
  );
  const [isClosing, setIsClosing] = useState(false);
  const pendingCloseCallbackRef = useRef<(() => void) | null>(null);
  const closeCallbackFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const menuContentRef = useRef<unknown>(null);

  const clearCloseCallbackFallback = useCallback(() => {
    if (closeCallbackFallbackRef.current !== null) {
      clearTimeout(closeCallbackFallbackRef.current);
      closeCallbackFallbackRef.current = null;
    }
  }, []);

  const runPendingCloseCallback = useCallback(() => {
    clearCloseCallbackFallback();
    const callback = pendingCloseCallbackRef.current;
    pendingCloseCallbackRef.current = null;
    callback?.();
  }, [clearCloseCallbackFallback]);

  // The debouncing of close is to accommodate exit animation
  const debouncedClose = useDebounce(() => {
    setIsOpen(false);
    setIsClosing(false);
    onClose?.();

    if (pendingCloseCallbackRef.current) {
      closeCallbackFallbackRef.current = setTimeout(
        runPendingCloseCallback,
        Platform.OS === "ios" ? AnimationDurations.moderate02 + 50 : 0
      );
    }
  }, AnimationDurations.moderate01);
  const handleDelayedCloseForAnimation = useCallback<CloseMenu>(
    (afterClose) => {
      if (afterClose) {
        pendingCloseCallbackRef.current = afterClose;
      }
      debouncedClose();
      setIsClosing(true);
    },
    [debouncedClose]
  );

  useEffect(() => {
    return () => {
      clearCloseCallbackFallback();
      pendingCloseCallbackRef.current = null;
    };
  }, [clearCloseCallbackFallback]);

  /**
   * If we have overridden 'isOpen', then we ignore any
   * trigger press
   */
  const shouldRespectTriggerPress = useMemo(() => {
    return overrideIsOpen === undefined;
  }, [overrideIsOpen]);

  const onTriggerPress = useCallback(() => {
    if (shouldRespectTriggerPress) {
      setIsOpen((state) => !state);
    }
  }, [shouldRespectTriggerPress]);

  /**
   * If we override Is Open, we need to react to changing values
   */
  useEffect(() => {
    if (overrideIsOpen !== undefined) {
      setIsOpen(overrideIsOpen);
      //If opening, cancel any running close animation and be open
      if (overrideIsOpen) {
        setIsClosing(false);
        debouncedClose.cancel();
      }
      //Also, if we are closing, we need to fire our animation
      if (!overrideIsOpen && isOpen) {
        handleDelayedCloseForAnimation();
      }
    }
  }, [debouncedClose, handleDelayedCloseForAnimation, isOpen, overrideIsOpen]);

  /**
   * If our overlay gets clicked, we need to maybe close
   * and always report
   */
  const handleOverlayClick = useCallback(() => {
    if (shouldRespectTriggerPress) {
      handleDelayedCloseForAnimation();
    }
    onClose?.();
  }, [handleDelayedCloseForAnimation, onClose, shouldRespectTriggerPress]);

  usePreventScroll({ isOpen });
  const closeMenuAndRestoreFocus = useMenuKeyboardNavigation({
    closeMenu: handleDelayedCloseForAnimation,
    isOpen,
    menuContentRef,
  });

  /**
   * Figure out if we are an action sheet or if we are a bubble
   */
  const screenWidth = Dimensions.get("window").width;
  const isActionSheet = neverOpenAsActionSheet ? false : screenWidth < 501;

  const contentProps = useMemo(() => {
    return {
      closeMenu: closeMenuAndRestoreFocus,
      isActionSheet,
      triggerWidth,
    };
  }, [closeMenuAndRestoreFocus, isActionSheet, triggerWidth]);

  /**
   * memoized context values
   */
  const menuContextValues: MenuContextType = useMemo(() => {
    return {
      isActionSheet,
      triggerWidth,
      showActionSheetDoneButton,
      onClose: handleOverlayClick,
    };
  }, [
    isActionSheet,
    triggerWidth,
    showActionSheetDoneButton,
    handleOverlayClick,
  ]);

  const containerCoordinates = isActionSheet ? mobileCoordinates : targetCoords;

  return (
    <>
      <View ref={triggerRef as any} onLayout={measureScreenPosition}>
        {trigger({ onPress: onTriggerPress, isOpen })}
      </View>
      <Modal
        visible={isOpen}
        transparent={true}
        onRequestClose={() => handleDelayedCloseForAnimation()}
        onDismiss={runPendingCloseCallback}
      >
        <MenuContext.Provider value={menuContextValues}>
          <Overlay
            onPress={handleOverlayClick}
            isClosing={isClosing}
            transparentBackground={!isActionSheet}
            blocksPointerEvents={!isClosing}
          />
          <Box
            ref={(element) => {
              menuContentRef.current = element;
            }}
            testID="menu-contents"
            position="absolute"
            opacity={containerCoordinates === null ? 0 : 1}
            {...containerCoordinates}
          >
            {isActionSheet ? (
              <View
                ref={modalBodyRef as any}
                onLayout={measureScreenPosition}
                style={{ height: "100%" }}
              >
                <ActionSheetContentContainer
                  isOpen={isOpen}
                  isClosing={isClosing}
                >
                  {content(contentProps)}
                </ActionSheetContentContainer>
              </View>
            ) : (
              <ModalContentContainer
                modalBodyRef={modalBodyRef}
                measureScreenPosition={measureScreenPosition}
                isOpen={isOpen}
                isClosing={isClosing}
                openPosition={openPosition}
              >
                {content(contentProps)}
              </ModalContentContainer>
            )}
          </Box>
        </MenuContext.Provider>
      </Modal>
    </>
  );
};

const mobileCoordinates: BubbleCoordinates = {
  bottom: 0,
  left: 0,
  right: 0,
  top: 168,
};

/**
 * Because this is a compound component we need to
 * compose it up down here before exporting it.
 */
MenuBase.Content = MenuContent;
export const Menu: MenuComponent = MenuBase;
